import type { BadgeVariant } from '@/types/common.types';
import type { FilterConfig } from '@/types/filters.types';
import type { DateRangePresetKey } from '@/lib/dateRangePresets';
import type { Credit, Payment, RecurringSchedule } from '@/services/payments.service';
import { leadSourceFilterConfig } from '@/hooks/filters/leadSourceOptions';
import { toLocaleDateStringInTz } from '@/lib/facilityTimezone';

/**
 * Sidebar filter set for the payments list pages: search plus the same eleven
 * filters legacy renders on these tabs (payment_credit_list-test.php:536-572 —
 * Date, Payment Type, Product, Status, Facility, Plan Type, Membership,
 * Package, Doctor, Coordinator, User), plus two v5 additions legacy does not
 * offer here:
 *
 *  - Referral Source — the PAYER's referral source, the same category/source
 *    tree the Patients list filters on, so it answers "payments made by
 *    patients who came from X".
 *  - Source — which CLIENT took the payment (Prospera, iOS, Android, Web App),
 *    read from api_request_metadata. Not to be confused with the above: this
 *    one is a property of the payment, that one of the person who made it.
 *
 * Both land in ListFilterService::applyPaymentFilters.
 *
 * Doctor and Coordinator take the account's configured role titles, so this is
 * a builder rather than a constant.
 */
export interface BuildPaymentFilterConfigsOptions {
  /**
   * Preset the Date filter opens on, e.g. 'today'. Omit for all-time.
   *
   * DELIBERATELY NOT THE DEFAULT, because the three callers of this builder
   * want three different things and one of them is load-bearing:
   *
   *   PaymentsPage             opts in to 'today'
   *   PaymentsAllocationsPage  all-time, unchanged -- a different list, and
   *                            not something to change as a side effect
   *   contact lists (dynamic
   *   list / audience builder) MUST stay all-time. A saved audience is
   *                            defined solely by what was saved, and
   *                            ContactListDetailPage hydrates only the keys
   *                            actually present in filterConditions. A list
   *                            saved with no date range would therefore
   *                            inherit whatever the config defaults to and
   *                            silently resolve to that window instead --
   *                            changing who is in an audience, with nothing
   *                            on screen to say so.
   */
  defaultDatePreset?: DateRangePresetKey;
}

export function buildPaymentFilterConfigs(
  doctorLabel: string,
  coordLabel: string,
  opts?: BuildPaymentFilterConfigsOptions,
): FilterConfig[] {
  return [
    { key: 'search', label: 'Search', type: 'search', allLabel: 'Search payments...' },
    // OPT-IN, and it has to be. The Payments page opens on today, because an
    // unbounded payments list is both the slowest query it can ask for and
    // rarely what anyone wants on arrival. But this builder is shared, and for
    // its other callers a default date range is wrong rather than merely
    // unwanted -- see the type doc on BuildPaymentFilterConfigsOptions.
    {
      key: 'daterange',
      label: 'Date',
      type: 'daterange',
      ...(opts?.defaultDatePreset ? { defaultPreset: opts.defaultDatePreset } : {}),
    },
    { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
    { key: 'payment-type', label: 'Payment Type', type: 'select', allLabel: 'All types', multi: true },
    { key: 'payment-status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
    // Which client took the payment, from api_request_metadata. Sits here with
    // the payment's own attributes and deliberately NOT next to Referral
    // Source at the bottom, which is a property of the PAYER, not the payment
    // — two filters both called some kind of "source" should not be adjacent.
    { key: 'payment-source', label: 'Source', type: 'select', allLabel: 'All sources', multi: true },
    { key: 'plan-type', label: 'Plan Type', type: 'select', allLabel: 'All plan types', multi: true },
    { key: 'product', label: 'Product', type: 'select', allLabel: 'All products', multi: true, collapsibleGroups: true },
    { key: 'membership-type', label: 'Membership', type: 'select', allLabel: 'All memberships', multi: true },
    { key: 'package-type', label: 'Package', type: 'select', allLabel: 'All packages', multi: true },
    { key: 'doctor', label: doctorLabel, type: 'select', allLabel: `All ${doctorLabel.toLowerCase()}s`, multi: true },
    { key: 'coordinator', label: coordLabel, type: 'select', allLabel: `All ${coordLabel.toLowerCase()}s`, multi: true },
    { key: 'users', label: 'User', type: 'select', allLabel: 'All users', multi: true },
    leadSourceFilterConfig('Referral Source', 'All sources'),
  ];
}

/**
 * The subtitle under the Purchased in App figure: what share of Cash Collected
 * the patient apps took.
 *
 * A share is the only useful framing for this number -- $40k of app sales reads
 * very differently against $50k than against $500k -- but a naive
 * Math.round() makes it LIE at the bottom of the range. Anything under 0.5%
 * rounds to "0% of cash collected", which sits directly beneath a tile showing
 * a real, non-zero dollar amount and reads as though the feature is broken.
 * That is what it was doing.
 *
 * So a non-zero amount never renders as 0%: it floors at "<1%". Zero really
 * means zero and gets the plain description instead of a percentage, because
 * "0%" next to $0.00 is noise.
 *
 * Rounds to one decimal below 10% and whole numbers above, so a small but real
 * share stays legible without giving a large one false precision.
 */
export function appShareHint(
  appPurchased: number | null | undefined,
  cashCollected: number | null | undefined,
): string {
  const app = Number(appPurchased ?? 0);
  const cash = Number(cashCollected ?? 0);

  if (!Number.isFinite(app) || !Number.isFinite(cash)) return 'Taken in the patient app';
  if (app <= 0 || cash <= 0) return 'Taken in the patient app';

  const share = (app / cash) * 100;

  // Guards the reported bug: a real amount must never read as 0%.
  if (share < 1) return '<1% of cash collected';
  // Cannot exceed the total it came out of; if it does, the two figures
  // disagree and saying so is better than printing 143%.
  if (share > 100) return 'Taken in the patient app';

  const rounded = share < 10 ? Math.round(share * 10) / 10 : Math.round(share);
  return `${rounded}% of cash collected`;
}

export interface CreditRow extends Credit {
  remaining: number;
}

/**
 * Filter keys exposed in the sidebar / API for the legacy six payment
 * statuses. Mirrors `html/lib/filters/filter-payment-status.php`.
 */
export type PaymentStatusFilterKey =
  | 'manually_entered'
  | 'pending'
  | 'settled'
  | 'voided'
  | 'declined'
  | 'refund'
  /** A declined membership payment whose month a membership credit paid. */
  | 'paid_by_credit';

/**
 * Display label + Badge variant for a payment row, derived from
 * `payment_transactions_authnet.status` (raw gateway status) plus the manual
 * `payments.void` flag. Mirrors the legacy `dt-full-payment-list-authnet.php`
 * mapping so the new V5 lists render the real legacy statuses (Manually
 * Entered, Pending, Settled, Voided, Declined, Refund Pending, Refunded)
 * instead of the invented Active/Voided binary.
 */
export interface PaymentStatusDisplay {
  label: string;
  variant: BadgeVariant;
  /** True when the payment was manually voided (greyed-out row in legacy). */
  voided: boolean;
  /** True for declined transactions (red row in legacy). */
  declined: boolean;
  /**
   * A declined membership payment whose month a membership credit paid:
   * still declined (no money came in) but nothing is owed, so it is shown
   * apart from the declines staff chase.
   */
  paidByCredit?: boolean;
}

export const PAID_BY_CREDIT_LABEL = 'Declined – paid by credit';

export function getPaymentStatusDisplay(
  payment: Pick<Payment, 'void' | 'transStatus' | 'isPaidByCredit'>,
): PaymentStatusDisplay {
  const isVoid =
    payment.void === true ||
    payment.void === '1' ||
    (payment.void as unknown) === 1;

  const raw = (payment.transStatus ?? '').trim();
  const declined = raw === 'declined';

  if (declined && payment.isPaidByCredit) {
    return { label: PAID_BY_CREDIT_LABEL, variant: 'info', voided: false, declined: true, paidByCredit: true };
  }

  if (declined) {
    return { label: 'Declined', variant: 'error', voided: false, declined: true };
  }

  if (isVoid || raw === 'voided') {
    return { label: 'Voided', variant: 'error', voided: true, declined: false };
  }

  // `transStatus` now carries the canonical `payments_list_index.paymentStatus`
  // value, whose vocabulary includes the legacy defaults `Manually Entered`,
  // `settled`, and `refund` alongside the raw gateway statuses.
  if (raw === '' || raw === 'Manually Entered') {
    return { label: 'Manual', variant: 'neutral', voided: false, declined: false };
  }

  if (raw === 'pending' || raw === 'capturedPendingSettlement') {
    return { label: 'Pending', variant: 'warning', voided: false, declined: false };
  }

  if (raw === 'settledSuccessfully' || raw === 'settled') {
    return { label: 'Settled', variant: 'success', voided: false, declined: false };
  }

  if (raw === 'refund') {
    return { label: 'Refund', variant: 'info', voided: false, declined: false };
  }

  if (raw === 'refundPendingSettlement') {
    return { label: 'Refund Pending', variant: 'warning', voided: false, declined: false };
  }

  if (raw === 'refundSettledSuccessfully') {
    return { label: 'Refunded', variant: 'info', voided: false, declined: false };
  }

  return { label: raw, variant: 'neutral', voided: false, declined: false };
}

export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

export function formatDate(value: string | null | undefined, tz?: string): string {
  if (!value) return '—';
  return toLocaleDateStringInTz(value, 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
  });
}

/**
 * Render a stored *calendar* date — `payments.dtmDate` is a DATE column, i.e.
 * a bare day with no time and no zone.
 *
 * Deliberately does NOT go through `new Date(...)` + `timeZone`. The API hands
 * dtmDate over as an instant (`pacificToUtc` turns the bare day into Pacific
 * midnight expressed in UTC), and re-projecting that instant into a display
 * zone can land a day off. The leading "YYYY-MM-DD" already *is* the stored
 * day, so read it literally and leave it alone.
 */
export function formatCalendarDate(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(value);
  // Constructed in browser-local time on purpose: the components are read back
  // with no `timeZone` option, so local-in / local-out is a lossless round trip.
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', options);
}

/**
 * The date to *show* for a payment.
 *
 * Prefers `entryDate` — a real timestamp of when the payment was taken, which
 * the API converts to a true instant — over `dtmDate`, the bare business-date
 * DATE column staff can override in the Date field. The two agree on almost
 * every row; where they don't, `entryDate` is the one that is reliably correct,
 * so it wins and gets a proper timezone conversion.
 *
 * Falls back to `dtmDate` (read literally, no timezone math) for legacy rows
 * that carry no entryDate.
 *
 * NOTE: this only governs what is displayed. Date-range filters and the default
 * sort still run on `dtmDate` server-side (ListFilterService::applyPaymentFilters),
 * so a row whose dtmDate is wrong still filters and sorts by that wrong day.
 */
export function formatPaymentDate(
  payment: { dtmDate?: string | null; entryDate?: string | null } | null | undefined,
  tz?: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string {
  if (!payment) return '—';
  if (payment.entryDate) {
    const entered = new Date(payment.entryDate);
    if (!Number.isNaN(entered.getTime())) {
      return toLocaleDateStringInTz(entered, 'en-US', { ...options, ...(tz ? { timeZone: tz } : {}) });
    }
  }
  return formatCalendarDate(payment.dtmDate, options);
}

/**
 * A payments grid's Type cell: "Type (Method)", except a payment covered
 * entirely by a gift card reads "Gift Card" and one that charged nothing reads
 * "Comp". Shared by the Payments tab and a plan's Payments card.
 */
export function paymentTypeLabel(row: Payment): string {
  const giftCard = row.giftCardTotal ?? 0;
  const charged = row.cardChargedAmount ?? row.curAmount;
  if (giftCard > 0 && charged === 0) return 'Gift Card';
  if (charged === 0) return 'Comp';
  if (row.strPayMethod && row.strPayType) return `${row.strPayType} (${row.strPayMethod})`;
  return row.strPayType ?? row.strPayMethod ?? '—';
}

export function toCreditRows(credits: Credit[] | undefined): CreditRow[] {
  return (credits ?? []).map((credit) => ({
    ...credit,
    remaining: credit.creditAmount - credit.redeemedAmount,
  }));
}

export function getPaymentPatientName(payment: Pick<Payment, 'patientID' | 'patientFirstName' | 'patientLastName'>): string {
  if (payment.patientFirstName || payment.patientLastName) {
    return `${payment.patientFirstName ?? ''} ${payment.patientLastName ?? ''}`.trim();
  }

  return payment.patientID ? `#${payment.patientID}` : 'Unknown patient';
}

export function getNextChargeDate(schedule: RecurringSchedule): string {
  if (!schedule.chargeDay) return '—';

  const now = new Date();
  const nextCharge = new Date(now.getFullYear(), now.getMonth(), schedule.chargeDay);
  if (nextCharge <= now) {
    nextCharge.setMonth(nextCharge.getMonth() + 1);
  }

  return nextCharge.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The save payload's `method` is a payment method's display NAME, which the
 * Record/Details modal resolves against the `/payments/method-options` catalog.
 * That catalog is filtered server-side to `active = 1` and
 * `typeIntentID IN (0, 1)`, so a deactivated method — or a gateway/terminal one,
 * such as the method a recurring charge inherits from its profile row — has no
 * entry to resolve against and the lookup yields nothing.
 *
 * The modal still SHOWS such a method: it renders a synthetic option from the
 * payment's own `strPayMethod`. But the Method control is disabled while editing,
 * so when the lookup came up empty the payload carried `method: ''` for a field
 * the user had no way to fill, and the save was rejected as "The method field is
 * required." Fall back to the title stored on the payment — the same value the
 * locked control is displaying.
 *
 * Create mode deliberately does not fall back: there is no stored payment to read
 * from, and the modal's own pre-submit check already blocks an unpicked method.
 */
export function resolveSaveMethodName(
  catalogName: string | undefined,
  isEditMode: boolean,
  storedPayMethod: string | null | undefined,
): string {
  if (catalogName) return catalogName;
  if (!isEditMode) return '';
  return (storedPayMethod ?? '').trim();
}
